/**
 * Ziyaret Hareket Tipi detayı (ZIYARET_TAKIP.md §2.5).
 *
 * <b>Kod yeni kayıtta girilir, sonra DEĞİŞTİRİLEMEZ</b> — geçmiş <c>VisitActivity</c>
 * satırları o koda bağlıdır ve değiştirmek iz kaydını sessizce yetim bırakırdı.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  DetailShell, Section, Grid, FieldText, FieldSelect, useIsViewMode,
} from './detail/DetailShell';

const API_BASE = '/api/visitactivitytypes';
const LIST_ROUTE = '/visit-activity-types';

const GROUP_OPTIONS = [
  { value: 'visit', label: 'Ziyaret' },
  { value: 'document', label: 'Belge' },
  { value: 'collection', label: 'Tahsilat' },
  { value: 'survey', label: 'Anket / Bulunurluk' },
  { value: 'gps', label: 'GPS' },
  { value: 'stock', label: 'Stok' },
  { value: 'day', label: 'Gün' },
  { value: 'other', label: 'Diğer' },
];

const COLOR_OPTIONS = [
  { value: 'accent', label: 'Mavi (belge)' },
  { value: 'green', label: 'Yeşil (ziyaret)' },
  { value: 'purple', label: 'Mor (tahsilat)' },
  { value: 'teal', label: 'Turkuaz (anket)' },
  { value: 'amber', label: 'Amber (stok/uyarı)' },
  { value: 'danger', label: 'Kırmızı (silme/iptal)' },
  { value: 'navy', label: 'Lacivert (gün)' },
  { value: 'gray', label: 'Gri (diğer)' },
];

const BOOL_OPTIONS = [
  { value: 'true', label: 'Evet' },
  { value: 'false', label: 'Hayır' },
];

const EMPTY = {
  code: '', nameTr: '', groupCode: 'other', verbTr: '', icon: '', color: 'gray',
  isTimelineVisible: 'true', isSuccessSignal: 'false', requiresReason: 'false',
  sortOrder: '0', isActive: 'true',
};

export function VisitActivityTypeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const isView = useIsViewMode() && !isNew;
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  usePageMeta(isNew ? 'Yeni Hareket Tipi' : 'Hareket Tipi Detayı', 'Saha İzleme akışındaki hareket sözlüğü.');

  const [form, setForm] = useState(EMPTY);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() });
        const payload = await res.json();
        const d = payload?.data ?? payload?.Data ?? payload;
        if (cancelled || !d) return;
        setForm({
          code: d.code ?? '',
          nameTr: d.nameTr ?? '',
          groupCode: d.groupCode ?? 'other',
          verbTr: d.verbTr ?? '',
          icon: d.icon ?? '',
          color: d.color ?? 'gray',
          isTimelineVisible: String(d.isTimelineVisible ?? true),
          isSuccessSignal: String(d.isSuccessSignal ?? false),
          requiresReason: String(d.requiresReason ?? false),
          sortOrder: String(d.sortOrder ?? 0),
          isActive: String(d.isActive ?? true),
        });
        setAudit({
          createdBy: d.createdBy, createdDate: d.createdDate,
          updatedBy: d.updatedBy, updatedDate: d.updatedDate,
        });
      } catch {
        if (!cancelled) showToast('error', 'Kayıt yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  async function handleSave() {
    setTried(true);
    if (!form.nameTr?.trim() || (isNew && !form.code?.trim())) {
      showToast('error', 'Kod ve ad zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...(isNew ? {} : { id }),
        ...(isNew ? { code: form.code.trim() } : {}),
        nameTr: form.nameTr.trim(),
        groupCode: form.groupCode,
        verbTr: form.verbTr || null,
        icon: form.icon || null,
        color: form.color,
        isTimelineVisible: form.isTimelineVisible === 'true',
        isSuccessSignal: form.isSuccessSignal === 'true',
        requiresReason: form.requiresReason === 'true',
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive === 'true',
      };
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || payload?.success === false || payload?.Success === false) {
        throw new Error(payload?.message || payload?.Message || 'Kaydedilemedi.');
      }
      showToast('success', 'Kaydedildi.');
      // Repo konvansiyonu: başarılı kayıttan sonra listeye dön.
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <DetailShell
      readOnly={isView}
      loading={loading}
      crumb={[
        { label: 'Ziyaret Hareket Tipleri', to: LIST_ROUTE },
        { label: isNew ? 'Yeni' : (isView ? 'Görüntüle' : 'Detay'), current: true },
      ]}
      audit={isNew ? null : audit}
      actions={!isView && canSave && (
        <button type="button" className="dps-btn primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      )}
    >
      <div className="dps-form">
        <Section icon="list" title="Hareket Tipi" subtitle="Kod sabittir; oluşturulduktan sonra değiştirilemez.">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="code" label="Kod" req mono
              disabled={!isNew}
              hint={isNew
                ? 'Örn. order_create. Küçük harf + alt çizgi. Sonradan DEĞİŞTİRİLEMEZ.'
                : 'Kod değiştirilemez — geçmiş hareket kayıtları bu koda bağlıdır.'} />
            <FieldText form={form} set={set} tried={tried} k="nameTr" label="Ekran Adı" req
              hint="Akışta ve raporda görünen ad. Örn. Sipariş Kaydı" />
            <FieldSelect form={form} set={set} k="groupCode" label="Grup" options={GROUP_OPTIONS}
              hint="Saha İzleme'deki filtre çipleri bu gruba göre çalışır." />
            <FieldText form={form} set={set} k="verbTr" label="Akış Cümlesi"
              hint="Örn. SİPARİŞ KAYIT YAPTI. Belge no/tutar varsa cümlenin başına eklenir." />
          </Grid>
        </Section>

        <Section icon="settings" title="Görünüm" subtitle="Akıştaki rozet ve renk.">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="icon" label="İkon" mono
              hint="Tabler ikon adı — örn. ti-file-invoice" />
            <FieldSelect form={form} set={set} k="color" label="Renk" options={COLOR_OPTIONS} />
            <FieldSelect form={form} set={set} k="isTimelineVisible" label="Akışta Görünsün" options={BOOL_OPTIONS}
              hint="GPS gibi dakikalık tekrar eden tipler için Hayır seçin — akışı boğar." />
            <FieldText form={form} set={set} k="sortOrder" label="Sıra" mono />
          </Grid>
        </Section>

        <Section icon="check" title="İş Kuralı" subtitle="Bu hareketin ziyaret sonucuna etkisi.">
          <Grid cols={2}>
            <FieldSelect form={form} set={set} k="isSuccessSignal" label="Başarı Sinyali" options={BOOL_OPTIONS}
              hint="Evet ise: bu hareketi içeren ziyaret BAŞARILI sayılır (belge/tahsilat kayıtları)." />
            <FieldSelect form={form} set={set} k="requiresReason" label="Neden Kodu Zorunlu" options={BOOL_OPTIONS}
              hint="Evet ise mobilde neden seçilmeden ziyaret kapatılamaz (örn. Satış Yok)." />
            <FieldSelect form={form} set={set} k="isActive" label="Durum"
              options={[{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }]}
              hint="Pasif tip cihazdan da düşer; geçmiş kayıtlar korunur." />
          </Grid>
        </Section>
      </div>

      <Toast toast={toast} />
    </DetailShell>
  );
}
